import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Switch from '@mui/material/Switch';
import { makeStyles } from '@mui/styles';
import InputText from '@/components/UI/InputText/InputText';
import { SKIP_DIALOG_AREAS, statusCampaign, workflowOptions } from '@/helpers/constants';
import useToastContext from '@/hooks/useToastContext';
import { getCampaignInfo, getCampaignRecipients, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import campaignServices from '@/services/campaignServices';
import styles from './DialogSkip.module.scss';

const useStyles = makeStyles(() => ({
  root: {
    height: '100vh',
  },
  content: {
    paddingTop: 0,
    width: '950px',
  },
  contentSkippeable: {
    paddingTop: 0,
    width: '800px',
  },
  stepper: {
    '& .MuiStepIcon-active': { color: 'green' },
    '& .MuiStepIcon-completed': { color: 'green' },
  },
}));

const DialogSkip = (props) => {
  const { close, workflow, recipients, campaignInfo, sendToValidate, skippable, isVANCR } = props;
  const { id } = useParams();
  const { t } = useTranslation();
  const classes = useStyles();
  const [areas, setAreas] = useState([]);
  const [steps, setSteps] = useState([]);
  const {
    approvalWorkflow,
    commentsApproverCauser,
    commentsApproverInformer,
    causerApprover,
    informerApprover,
    causerPreApprover,
    informerPreApprover,
    causerCheckApprover,
    informerCheckApprover,
    campaignManager,
    vanCommitteeCoordinator,
    e3ChiefEngineer,
    e2QMBInformer,
  } = recipients;
  const {
    IN_CREATION,
    IN_CAUSER_APPROVAL,
    IN_INFORMER_APPROVAL,
    IN_VAN_CAUSER_APPROVAL,
    IN_CAUSER_PRE_APPROVAL,
    IN_CAUSER_CHECK,
    IN_INFORMER_PRE_APPROVAL,
    IN_INFORMER_CHECK,
    IN_PLANT_VALIDATION,
    IN_VAN_PLANT_VALIDATION,
    REQUESTED_FOR_CHANGES,
    IN_PREPARATION_FOR_REVIEW,
    IN_REVIEW,
    IN_CARLINE_CONFIRMATION,
    COMPLETED,
  } = statusCampaign;
  const {
    E2C,
    E4C_E3C_E2C,
  } = workflowOptions;
  const ShortStatuses = {
    CRE: { status: IN_CREATION, assignedUser: campaignInfo.contactPerson },
    E2C: { status: IN_CAUSER_APPROVAL, assignedUser: causerApprover },
    E2I: { status: IN_INFORMER_APPROVAL, assignedUser: informerApprover },
    E3C: { status: IN_CAUSER_PRE_APPROVAL, assignedUser: causerPreApprover },
    E3I: { status: IN_INFORMER_PRE_APPROVAL, assignedUser: informerPreApprover },
    E4C: { status: IN_CAUSER_CHECK, assignedUser: causerCheckApprover },
    E4I: { status: IN_INFORMER_CHECK, assignedUser: informerCheckApprover },
    IPV: { status: IN_PLANT_VALIDATION, assignedUser: campaignManager },
    IVPV: { status: IN_VAN_PLANT_VALIDATION, assignedUser: e2QMBInformer },
    RFC: { status: REQUESTED_FOR_CHANGES, assignedUser: null },
    DYNAMIC: { status: IN_CREATION, assignedUser: null },
    IPFR: { status: IN_PREPARATION_FOR_REVIEW, assignedUser: null },
    IR: { status: IN_REVIEW, assignedUser: vanCommitteeCoordinator },
    ICA: { status: IN_CAUSER_APPROVAL, assignedUser: causerPreApprover },
    IVCA: { status: IN_VAN_CAUSER_APPROVAL, assignedUser: causerApprover },
    ICC: { status: IN_CARLINE_CONFIRMATION, assignedUser: e3ChiefEngineer },
    FIN: { status: COMPLETED, assignedUser: null },
  };

  const oneWayShort = E2C === approvalWorkflow;
  const oneWay = [E2C, E4C_E3C_E2C].includes(approvalWorkflow);
  const [form, setForm] = useState({
    commentsInformerApprover: { value: commentsApproverInformer, disabled: false },
    commentsCauserApprover: { value: commentsApproverCauser, disabled: false },
    allReapprovalsNeeded: false,
    reapprovals: [],
  });
  const { addToast } = useToastContext();

  const stepLabel = (status, version) => (status === IN_CREATION ? `CR Version ${version}` : `${t(`campaign.${status}`)}`);
  const stepCompleted = (status, action) => status === IN_CREATION || (action && action !== REQUESTED_FOR_CHANGES);
  const stepWithSwitch = (status, action) => !oneWayShort && ![IN_CREATION, COMPLETED].includes(status) && action === 'APPROVED';
  const stepActive = (status, action) => action && action !== 'APPROVED' && ![REQUESTED_FOR_CHANGES, COMPLETED].includes(status);
  const stepAction = (action, version) => (action && action !== REQUESTED_FOR_CHANGES ? `${action} V${version} `?.toLowerCase() : '');
  const stepArea = (status) => SKIP_DIALOG_AREAS.find(({ statuses }) => statuses.includes(status))?.name;
  const assignedUser = (status) => {
    const parts = ShortStatuses[status]?.assignedUser?.summary?.toLowerCase()?.split(',');
    const lastPart = parts?.pop()?.toUpperCase();

    return parts
      ? `${parts[0]}, ${parts[1]}, ${lastPart}`
      : '';
  };

  const findChangeRequester = () => workflow.statuses.find(({ actionTaken }) => actionTaken === REQUESTED_FOR_CHANGES);

  const someApprovalNeeded = (approverType, reaprovals) => reaprovals.some((s) => s.includes(approverType));

  const requesterIs = (approverType) => findChangeRequester().statusCode.includes(approverType);

  const commentIsEditable = (approverType, reaprovals) => someApprovalNeeded(approverType, reaprovals) || requesterIs(approverType);

  const getApprovals = () => workflow.statuses
    .filter(({ actionTaken }) => actionTaken === 'APPROVED')
    .map(({ statusCode }) => statusCode);

  const approvalsNewState = (label) => {
    const { reapprovals, allReapprovalsNeeded } = form;

    if (label) {
      return reapprovals.includes(label)
        ? reapprovals.filter((reapproval) => reapproval !== label)
        : [...reapprovals, label];
    }

    return allReapprovalsNeeded
      ? []
      : getApprovals();
  };

  const handleSwitchChange = (label) => {
    const { allReapprovalsNeeded } = form;
    const newReaprovals = approvalsNewState(label);

    const newState = {
      ...form,
      reapprovals: newReaprovals,
      allReapprovalsNeeded: allReapprovalsNeeded ? !allReapprovalsNeeded : newReaprovals.length === getApprovals().length,
      commentsInformerApprover: {
        value: commentIsEditable('INFORMER', newReaprovals) ? form.commentsInformerApprover.value : commentsApproverInformer,
        disabled: !commentIsEditable('INFORMER', newReaprovals),
      },
      commentsCauserApprover: {
        value: commentIsEditable('CAUSER', newReaprovals) ? form.commentsCauserApprover.value : commentsApproverCauser,
        disabled: !commentIsEditable('CAUSER', newReaprovals),
      },
    };

    setForm(newState);
  };

  const handleCommentsChange = (label, value) => {
    setForm({ ...form, [label]: { ...form[label], value } });
  };

  const findStepStatus = (status) => (workflow.statuses
    .find((s) => s?.statusCode === ShortStatuses?.[status]?.status && s?.actionTaken !== REQUESTED_FOR_CHANGES)
  );

  const getPreviousVersion = (statusCode, version, previous) => (statusCode === IN_CREATION ? version - 1 : previous);

  const setComments = () => {
    const newState = {
      ...form,
      commentsInformerApprover: { value: commentsApproverInformer, disabled: requesterIs('CAUSER') },
      commentsCauserApprover: { value: commentsApproverCauser, disabled: requesterIs('INFORMER') },
    };
    setForm(newState);
  };

  const prepreStatuses = () => {
    const approvers = approvalWorkflow.split('_');
    const workflowStatuses = ['CRE', ...(approvers || []), 'FIN'];
    let previousVersion = 1;

    return workflowStatuses?.map((status, index) => {
      const { actionTaken, statusCode, version } = findStepStatus(status) || {};
      previousVersion = getPreviousVersion(statusCode, version, previousVersion);

      return {
        step: index + 1,
        status: statusCode,
        label: stepLabel(statusCode, previousVersion),
        assignedUser: assignedUser(status),
        isRequester: statusCode === findChangeRequester().statusCode,
        action: stepAction(actionTaken, previousVersion),
        completed: stepCompleted(statusCode, actionTaken),
        active: stepActive(statusCode, actionTaken),
        hasSwitch: stepWithSwitch(statusCode, actionTaken),
        area: stepArea(status),
      };
    });
  };

  const sendToApproval = () => {
    const payload = {
      comments: {
        commentsApproverInformer: form?.commentsInformerApprover.value,
        commentsApproverCauser: form?.commentsCauserApprover.value,
      },
      approvals: { statuses: form?.reapprovals },
    };
    const saveApprovals = campaignServices?.saveApprovalsSkip(id, payload.approvals);
    const updateRecipients = campaignServices?.updateCampaignRecipients(id, payload.comments, 'comments', campaignInfo.type, campaignInfo.creationDate);
    const promises = isVANCR ? [saveApprovals] : [updateRecipients, saveApprovals];

    Promise.all(promises)
      .then(() => {
        sendToValidate();
        close();
      })
      .catch((error) => addToast(error.message, 'ERROR'));
  };

  useEffect(() => {
    const filteredAreas = SKIP_DIALOG_AREAS.filter(({ name }) => !oneWay || name !== 'informers');
    setAreas(filteredAreas);
    setSteps(prepreStatuses());
    setComments();

    if (!skippable) {
      const allApprovals = getApprovals();
      const newState = {
        ...form,
        reapprovals: allApprovals,
        allReapprovalsNeeded: false,
        commentsInformerApprover: {
          value: commentsApproverInformer,
          disabled: !commentIsEditable('INFORMER', allApprovals),
        },
        commentsCauserApprover: {
          value: commentsApproverCauser,
          disabled: !commentIsEditable('CAUSER', allApprovals),
        },
      };
      setForm(newState);
    }
  }, []);

  return (
    <Dialog
      open
      maxWidth="md"
      aria-labelledby="form-dialog-title"
      className={classes.root}
    >
      <DialogTitle id="form-dialog-title">{t('summary.changeNotification')}</DialogTitle>
      <DialogContent className={skippable ? classes.content : classes.contentSkippeable}>
        {skippable
          ? (
            <div className={styles.DialogSkipContent}>
              <div>
                <p>{t(`summary.reapprovalMessage${oneWayShort ? 'Short' : 'Long'}`)}</p>
                {!oneWayShort && (
                  <div className={styles.AllApprovalsNeeded}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={form.allReapprovalsNeeded}
                          color="primary"
                          name="reapprovalAll"
                          onChange={() => handleSwitchChange()}
                        />
                      )}
                      label={t('summary.reapprovalLabel')}
                    />
                  </div>
                )}
              </div>
              {areas.map((area) => (
                <div key={area.name} className={styles.AreaContainer}>
                  <div className={styles.SteppersContainer}>
                    {steps.map(
                      (step, index) => area.name === step.area && (
                        <div className={styles.SwitchStepContainer}>
                          {!oneWayShort && (
                            <div className={styles.SwitchContainer}>
                              {step.hasSwitch && (
                                <Switch
                                  checked={form.reapprovals.includes(step.status)}
                                  name={`approval-switch-${index}`}
                                  color="primary"
                                  onChange={() => handleSwitchChange(step.status)}
                                />
                              )}
                            </div>
                          )}
                          <Stepper nonLinear orientation="vertical" className={classes.stepper}>
                            <Step
                              key={step.label}
                              expanded
                              active={step.active && !form.reapprovals.includes(step.status)}
                              completed={step.completed && !form.reapprovals.includes(step.status)}
                            >
                              <StepLabel icon={step.step}>
                                <strong>{step.label}</strong><span>{step.isRequester ? ` (${t('summary.changeRequested')})` : ''}</span>
                              </StepLabel>
                              <StepContent className={styles.StepContent}>
                                <div>{step.assignedUser}</div>
                                <div>{step.action}</div>
                              </StepContent>
                            </Step>
                            <span />
                          </Stepper>
                        </div>
                      ),
                    )}
                  </div>
                  <div className={styles.CommentsContainer}>
                    {area.comment && (
                      <InputText
                        disabled={form[area.comment].disabled}
                        className={styles.Fields}
                        multiline
                        rows={5}
                        maxRows={5}
                        id={`skip-dialog-input-${area.comment}`}
                        label={t(`recipients.${area.comment}`)}
                        handleChange={(ev) => handleCommentsChange(area.comment, ev.target.value)}
                        value={form[area.comment].value}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
          : (
            <div className={styles.DialogSkipContent}>
              <p>{t('dialog.mandatoryReaproval')}</p>
            </div>
          )}
      </DialogContent>
      <DialogActions className={styles.DialogSkipActions}>
        <Button variant="contained" color="primary" onClick={sendToApproval}>
          {t('dialog.send')}
        </Button>
        <Button color="primary" onClick={close}>{t('dialog.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  recipients: getCampaignRecipients(state),
  campaignInfo: getCampaignInfo(state),
  isVANCR: getIsVANCR(state),
});

export default connect(mapStateToProps)(DialogSkip);
